import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { AuthTokenStore } from '../services/auth-token.store';
import { environment } from '../../../environments/environment';

/**
 * The only paths the customer's access token may be sent to.
 *
 * **This is an allowlist on purpose (PS-6495, D4).** A blocklist — "everywhere except
 * `/document`" — would hand the token to every endpoint anyone adds later, silently. With an
 * allowlist, widening the token's reach costs a line here and is therefore visible in review.
 *
 * `/document` must never appear below: it is the token-free endpoint that every customer reaches
 * with nothing but a `uid`, so a bearer buys no access there and only widens where a token can
 * leak. `/rest/login` must not appear either — it is where the token comes FROM, and at that point
 * there is none to send.
 *
 * Note for anyone tempted to rely on the token's scope as a safety net: it is not one. The
 * bookingmgmt `AuthPolicies` contains no `SCOPE_*` check (roles only), so no resource server on
 * this path reads the scope. The only real limiter is the phone-service-bound ACL on the booking
 * endpoint. The `phoneServiceId` match against the callnote that PS-6646 added before the order
 * (`GuidedSetup.resolvePhoneService`) is NOT a second one: it runs in the browser, guards against
 * mix-ups, and anyone holding a token can skip it. Widen this list on the strength of one limiter.
 *
 * `/localization/v2/i18n` is listed as the exact endpoint, not as `/localization/`: the legal links
 * on the order screen (`LegalLinksService`) are read from it, and it answers 401 without a bearer.
 * The page's translations do NOT come from here — they arrive in the callnote payload.
 *
 * The own-number verification is listed endpoint by endpoint - validate, trigger, manual request,
 * and poll/cancel by pattern - mirroring the external LB whitelist. NOT as the sub-tree
 * `/phoneservice/v3/cli-verification/`: next to these five live the voice bot's `/code` and
 * `/call-outcome`, the cron's `/attempts/expire` and the support `/numbers/{n}/...` endpoints, all
 * guarded by client authority rather than by a phone-service check, and none of them has any
 * business receiving a customer token.
 *
 * `/customersupport/v1/logmessage` is the log bridge (`LoggingService`), which requires auth. This
 * interceptor is the single place the bearer is attached — the logging service sets no header of
 * its own.
 *
 * Matching (L6, code review 2): the EXACT paths below must equal the request path; only the
 * prefixes match by `startsWith`, and each ends in `/` so `/bookingmgmt` cannot match
 * `/bookingmgmtx`. The request must also go to this page's own origin or to the API origin
 * (`environment.baseUrl`) - a path match on a foreign host never gets the token.
 */
const TOKEN_SCOPED_EXACT_PATHS: readonly string[] = [
	'/authorization/v2/oauth/check_token',
	'/localization/v2/i18n',
	'/customersupport/v1/logmessage',
	'/phoneservice/v3/cli-verification/number/validate',
	'/phoneservice/v3/cli-verification/attempts',
	'/phoneservice/v3/cli-verification/manual-requests',
];

const TOKEN_SCOPED_PATH_PREFIXES: readonly string[] = ['/bookingmgmt/'];

/** Poll and cancel of one attempt; the id is numeric, nothing else matches. */
const TOKEN_SCOPED_PATH_PATTERNS: readonly RegExp[] = [/^\/phoneservice\/v3\/cli-verification\/attempts\/\d+(\/cancel)?$/];

/** Never gets the bearer, checked before the allowlist: the link uid in the body is the only credential. */
const TOKEN_DENIED_PATH = '/bookingmgmt/v2/outbox/linkorders';

/**
 * PS-6495 — attaches the customer's bearer to the allowlisted paths and to nothing else.
 */
export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
	const store = inject(AuthTokenStore);
	const token = store.token;
	if (!token) {
		return next(req);
	}

	if (!isTokenScoped(req.url)) {
		return next(req);
	}

	store.touch();
	return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
};

/**
 * Relative URLs resolve against the page, so they are same-origin by construction - that is the
 * dev proxy's case, where `baseUrl` is `''`. Absolute URLs must name one of the two trusted
 * origins. An unparseable URL is denied rather than guessed.
 */
function isTokenScoped(url: string): boolean {
	let parsed: URL;
	try {
		parsed = new URL(url, window.location.origin);
	} catch {
		return false;
	}
	if (!trustedOrigins().includes(parsed.origin)) {
		return false;
	}
	const path = parsed.pathname;
	if (path === TOKEN_DENIED_PATH || path.startsWith(`${TOKEN_DENIED_PATH}/`)) {
		return false;
	}
	return (
		TOKEN_SCOPED_EXACT_PATHS.includes(path) ||
		TOKEN_SCOPED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix)) ||
		TOKEN_SCOPED_PATH_PATTERNS.some((pattern) => pattern.test(path))
	);
}

/** The page's own origin, plus the API origin when `baseUrl` is an absolute URL. */
function trustedOrigins(): string[] {
	const origins = [window.location.origin];
	try {
		origins.push(new URL(environment.baseUrl).origin);
	} catch {
		/** `''` (dev proxy) or an unreplaced placeholder: relative requests only. */
	}
	return origins;
}
